import type { OpsUsageOverviewResponse } from '@nadoutong/contracts';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useOpsUsageOverview } from '../../api/opsUsage.ts';
import { usePricing } from '../../api/pricing.ts';
import {
  formatCnyCents,
  formatLocalTimestamp,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
} from '../../components/formatters.ts';
import { CustomerCell } from '../../components/identity/CustomerCell.tsx';
import { useT } from '../../i18n/index.ts';
import { KpiCard, Panel, TBody, Table, Td, Th, THead, Tr } from '../../ui/index.ts';
import type { MetricsTabContext } from './hubState.ts';

/**
 * 用量 tab (epic #1643 S5): the raw usage overview absorbed from the
 * standalone `/customers/usage` page — KPI wall + top-tenant / by-model
 * / by-brand / by-upstream / by-key tables from `/v1/ops/usage/overview`.
 * Window/brand/custom bounds come from the hub's shared filter row; the
 * usage-specific drill filters stay URL-backed under `usage`-prefixed
 * params (`usageAccountId` / `usageEmail`) so they never collide with
 * the hub's own params.
 */

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');

function formatTokens(n: number): string {
  return TOKEN_FORMATTER.format(n);
}

function formatTokenPair(inputTokens: number, outputTokens: number): string {
  return `${formatTokens(inputTokens)} / ${formatTokens(outputTokens)}`;
}

function formatPct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export function UsageTab({ context }: { readonly context: MetricsTabContext }): JSX.Element {
  const t = useT();
  const [params] = useSearchParams();
  const accountId = params.get('usageAccountId') ?? undefined;
  const email = params.get('usageEmail') ?? undefined;
  const { window, brand, customFrom, customTo } = context;
  const custom = window === 'custom';
  const query = useOpsUsageOverview({
    window,
    from: custom ? customFrom : undefined,
    to: custom ? customTo : undefined,
    accountId,
    email,
    brand: brand === '' ? undefined : brand,
  });
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;

  return (
    <div className="flex flex-col gap-6" data-testid="metrics-usage-tab">
      {query.isLoading ? (
        <div
          className="rounded-lg border border-line-soft bg-card p-6 text-body text-paper-low"
          data-testid="metrics-usage-loading"
        >
          {t('metrics.usage.loading')}
        </div>
      ) : query.isError ? (
        <div
          className="rounded-lg border border-bad-line bg-bad-dim/50 p-6 text-body text-bad-bright"
          data-testid="metrics-usage-error"
        >
          {t('metrics.usage.error', {
            message: query.error instanceof Error ? query.error.message : 'unknown error',
          })}
        </div>
      ) : query.data ? (
        <UsageReady data={query.data} cnyPerUsd={cnyPerUsd} />
      ) : (
        <div
          className="rounded-lg border border-line-soft bg-card p-6 text-body text-paper-low"
          data-testid="metrics-usage-empty"
        >
          {t('metrics.usage.empty')}
        </div>
      )}
    </div>
  );
}

function UsageReady({
  data,
  cnyPerUsd,
}: {
  readonly data: OpsUsageOverviewResponse;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          testId="metrics-usage-requests"
          label={t('metrics.usage.metric.requests')}
          value={formatTokens(data.totals.requestCount)}
          sub={t('metrics.usage.metric.requests.sub', {
            success: formatTokens(data.totals.successCount),
            error: formatTokens(data.totals.errorCount),
          })}
        />
        <MetricCard
          testId="metrics-usage-tokens"
          label={t('metrics.usage.metric.tokens')}
          value={formatTokens(data.totals.inputTokens + data.totals.outputTokens)}
          sub={t('metrics.usage.metric.tokens.sub', {
            input: formatTokens(data.totals.inputTokens),
            output: formatTokens(data.totals.outputTokens),
          })}
        />
        <MetricCard
          testId="metrics-usage-cache"
          label={t('metrics.usage.metric.cache')}
          value={formatTokens(data.totals.cacheReadTokens + data.totals.cacheWriteTokens)}
          sub={t('metrics.usage.metric.cache.sub', {
            read: formatTokens(data.totals.cacheReadTokens),
            write: formatTokens(data.totals.cacheWriteTokens),
          })}
        />
        <MetricCard
          testId="metrics-usage-charged"
          label={t('metrics.usage.metric.charged')}
          value={formatUsdMicrosOrCentsAsCnyDisplay(
            data.totals.usdChargedMicros,
            data.totals.usdChargedCents,
            cnyPerUsd, false)}
          sub={t('metrics.usage.metric.charged.sub', {
            amount: formatUsdMicrosOrCentsAsCnyDisplay(
              data.totals.costUsdMicros,
              data.totals.costUsdCents,
              cnyPerUsd, false),
          })}
        />
        <MetricCard
          testId="metrics-usage-upstream-cost"
          label={t('metrics.usage.metric.upstream_cost')}
          value={formatCnyCents(data.totals.cnyUpstreamCostCents, false)}
          sub={t('metrics.usage.metric.upstream_cost.sub')}
        />
        <MetricCard
          testId="metrics-usage-l2-profit"
          label={t('metrics.usage.metric.l2_profit')}
          value={formatCnyCents(data.totals.l2ProfitCnyCents, true)}
          sub={t('metrics.usage.metric.l2_profit.sub', {
            pct: formatPct(data.totals.l2MarginPct),
          })}
        />
        <MetricCard
          testId="metrics-usage-gross-margin"
          label={t('metrics.usage.metric.gross_margin')}
          value={formatCnyCents(data.totals.totalGrossMarginCnyCents, true)}
          sub={t('metrics.usage.metric.gross_margin.sub')}
        />
        <MetricCard
          testId="metrics-usage-window"
          label={t('metrics.usage.metric.window')}
          value={formatWindow(data.window.from, data.window.to)}
          sub={t('metrics.usage.metric.window.sub')}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <TenantsTable data={data} cnyPerUsd={cnyPerUsd} />
        <ModelsTable data={data} cnyPerUsd={cnyPerUsd} />
        <BrandsTable data={data} cnyPerUsd={cnyPerUsd} />
        <UpstreamsTable data={data} cnyPerUsd={cnyPerUsd} />
        <KeysTable data={data} cnyPerUsd={cnyPerUsd} />
      </div>
    </>
  );
}

function MetricCard({
  label,
  value,
  sub,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly testId: string;
}): JSX.Element {
  return <KpiCard label={label} value={value} delta={{ text: sub, tone: 'neutral' }} testId={testId} />;
}

function TenantsTable({
  data,
  cnyPerUsd,
}: {
  readonly data: OpsUsageOverviewResponse;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <UsageTable
      title={t('metrics.usage.table.tenants')}
      empty={t('metrics.usage.empty.tenants')}
      columns={[
        t('metrics.usage.col.object'),
        t('metrics.usage.col.requests'),
        t('metrics.usage.col.io'),
        t('metrics.usage.col.charged'),
        t('metrics.usage.col.l2_profit'),
      ]}
    >
      {data.tenants.map((row) => (
        <Tr key={row.accountId}>
          <Td>
            {/* #1008 drill-through: the tenant links to its account detail,
                and a second link jumps to that account's request log. */}
            <CustomerCell
              accountId={row.accountId}
              testId={`metrics-usage-tenant-customer-${row.accountId}`}
              secondary={
                <div>
                  <Link
                    to={`/customers/accounts/${encodeURIComponent(row.accountId)}`}
                    className="font-medium text-paper transition-colors duration-120 hover:text-signal"
                    data-testid={`metrics-usage-tenant-account-${row.accountId}`}
                  >
                    {row.email || row.accountId}
                  </Link>
                  <div className="font-mono text-micro text-paper-low">{row.accountId}</div>
                  <Link
                    to={`/customers/requests?accountId=${encodeURIComponent(row.accountId)}`}
                    className="mt-0.5 inline-block font-mono text-micro text-signal transition-colors duration-120 hover:underline"
                    data-testid={`metrics-usage-tenant-requests-${row.accountId}`}
                  >
                    {t('metrics.usage.view_requests')}
                  </Link>
                </div>
              }
            />
          </Td>
          <Td num>{formatTokens(row.requestCount)}</Td>
          <Td num>{formatTokenPair(row.inputTokens, row.outputTokens)}</Td>
          <Td num>
            {formatUsdMicrosOrCentsAsCnyMetered(row.usageUsdChargedMicros, row.usageUsdChargedCents, cnyPerUsd, false)}
          </Td>
          <Td num className="whitespace-nowrap">{formatCnyCents(row.l2ProfitCnyCents, true)}</Td>
        </Tr>
      ))}
    </UsageTable>
  );
}

function ModelsTable({
  data,
  cnyPerUsd,
}: {
  readonly data: OpsUsageOverviewResponse;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <UsageTable
      title={t('metrics.usage.table.models')}
      empty={t('metrics.usage.empty.models')}
      columns={[
        t('metrics.usage.col.object'),
        t('metrics.usage.col.requests'),
        t('metrics.usage.col.io'),
        t('metrics.usage.col.charged'),
        t('metrics.usage.col.l2_profit'),
      ]}
    >
      {data.models.map((row) => (
        <Tr key={`${row.vendor}:${row.model}`}>
          <Td>
            <div className="font-medium text-paper">{row.model}</div>
            <div className="text-micro text-paper-low">{row.vendor}</div>
          </Td>
          <Td num>{formatTokens(row.requestCount)}</Td>
          <Td num>{formatTokenPair(row.inputTokens, row.outputTokens)}</Td>
          <Td num>
            {formatUsdMicrosOrCentsAsCnyMetered(row.usageUsdChargedMicros, row.usageUsdChargedCents, cnyPerUsd, false)}
          </Td>
          <Td num className="whitespace-nowrap">{formatCnyCents(row.l2ProfitCnyCents, true)}</Td>
        </Tr>
      ))}
    </UsageTable>
  );
}

function BrandsTable({
  data,
  cnyPerUsd,
}: {
  readonly data: OpsUsageOverviewResponse;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <UsageTable
      title={t('metrics.usage.table.brands')}
      empty={t('metrics.usage.empty.brands')}
      columns={[
        t('metrics.usage.col.object'),
        t('metrics.usage.col.requests'),
        t('metrics.usage.col.io'),
        t('metrics.usage.col.charged'),
        t('metrics.usage.col.l2_profit'),
      ]}
    >
      {(data.brands ?? []).map((row) => (
        <Tr key={row.brand}>
          <Td>
            <div className="font-medium text-paper">{row.brand === 'nxtpath' ? 'Nxtpath' : 'Nadoutong'}</div>
            <div className="font-mono text-micro text-paper-low">{row.brand}</div>
          </Td>
          <Td num>{formatTokens(row.requestCount)}</Td>
          <Td num>{formatTokenPair(row.inputTokens, row.outputTokens)}</Td>
          <Td num>
            {formatUsdMicrosOrCentsAsCnyMetered(row.usdChargedMicros, row.usdChargedCents, cnyPerUsd, false)}
          </Td>
          <Td num className="whitespace-nowrap">{formatCnyCents(row.l2ProfitCnyCents, true)}</Td>
        </Tr>
      ))}
    </UsageTable>
  );
}

function UpstreamsTable({
  data,
  cnyPerUsd,
}: {
  readonly data: OpsUsageOverviewResponse;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <UsageTable
      title={t('metrics.usage.table.upstreams')}
      empty={t('metrics.usage.empty.upstreams')}
      columns={[
        t('metrics.usage.col.object'),
        t('metrics.usage.col.requests'),
        t('metrics.usage.col.io'),
        t('metrics.usage.col.gateway_cost'),
        t('metrics.usage.col.errors'),
      ]}
    >
      {data.upstreams.map((row) => (
        <Tr key={row.upstreamId}>
          <Td>
            <div className="font-medium text-paper">{row.upstreamName || row.upstreamId}</div>
            <div className="font-mono text-micro text-paper-low">{row.upstreamId}</div>
          </Td>
          <Td num>{formatTokens(row.requestCount)}</Td>
          <Td num>{formatTokenPair(row.inputTokens, row.outputTokens)}</Td>
          <Td num>
            {formatUsdMicrosOrCentsAsCnyMetered(row.costUsdMicros, row.costUsdCents, cnyPerUsd, false)}
          </Td>
          <Td num>{formatTokens(row.errorCount)}</Td>
        </Tr>
      ))}
    </UsageTable>
  );
}

function KeysTable({
  data,
  cnyPerUsd,
}: {
  readonly data: OpsUsageOverviewResponse;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <UsageTable
      title={t('metrics.usage.table.keys')}
      empty={t('metrics.usage.empty.keys')}
      columns={[
        t('metrics.usage.col.object'),
        t('metrics.usage.col.requests'),
        t('metrics.usage.col.io'),
        t('metrics.usage.col.cache_rw'),
        t('metrics.usage.col.gateway_cost'),
      ]}
    >
      {data.keys.map((row) => (
        <Tr key={row.apiKeyId}>
          <Td>
            <div className="font-medium text-paper">{row.apiKeyName ?? t('metrics.usage.unnamed_key')}</div>
            <div className="text-micro text-paper-low">
              {row.channelName ?? row.channelSlug ?? t('metrics.usage.unknown_channel')}
            </div>
          </Td>
          <Td num>{formatTokens(row.requestCount)}</Td>
          <Td num>{formatTokenPair(row.inputTokens, row.outputTokens)}</Td>
          <Td num>{formatTokenPair(row.cacheReadTokens, row.cacheWriteTokens)}</Td>
          <Td num>
            {formatUsdMicrosOrCentsAsCnyMetered(row.costUsdMicros, row.costUsdCents, cnyPerUsd, false)}
          </Td>
        </Tr>
      ))}
    </UsageTable>
  );
}

function UsageTable({
  title,
  empty,
  columns,
  children,
}: {
  readonly title: string;
  readonly empty: string;
  readonly columns: readonly [string, string, string, string, string];
  readonly children: ReactNode;
}): JSX.Element {
  const count = Array.isArray(children) ? children.length : 1;
  return (
    <Panel caption={title}>
      {count === 0 ? (
        <p className="m-0 p-4 text-body text-paper-low">{empty}</p>
      ) : (
        <Table className="border-none">
          <THead>
            <Tr interactive={false}>
              {columns.map((column, index) => (
                <Th key={column} num={index !== 0}>
                  {column}
                </Th>
              ))}
            </Tr>
          </THead>
          <TBody>{children}</TBody>
        </Table>
      )}
    </Panel>
  );
}

function formatWindow(from: string, to: string): string {
  return `${formatLocalTimestamp(from)} → ${formatLocalTimestamp(to)}`;
}
